import React, { useState, memo } from 'react';
import toast from 'react-hot-toast';
import { useConcessionTypes } from '../../../utils/concessions';

const DEFAULT_FORM = {
  name: '',
  description: '',
  instructor: '',
  location: '',
  startTime: '',
  duration: 60,
  maxCapacity: 20,
  concessionsRequired: 1,
  // Every class must draw from a named concession purse (required)
  concessionType: '',
  scheduleType: 'one-off',
  recurringPattern: 'none',
  recurringDays: [],
  recurringStartDate: '',
  recurringEndDate: '',
  recurringInterval: 1
};

/**
 * AddClassModal - Isolated modal component for adding classes
 * Manages its own form state to prevent parent re-renders on every keystroke
 */
const AddClassModal = memo(({ isOpen, onClose, onSubmit, isCreating, initialStartTime }) => {
  const [form, setForm] = useState(DEFAULT_FORM);
  const { types: concessionTypes } = useConcessionTypes(isOpen);

  const handleClose = () => {
    setForm(DEFAULT_FORM);
    onClose();
  };

  const handleSubmit = () => {
    if (!form.concessionType) {
      toast.error('Please choose a concession type for this class');
      return;
    }
    onSubmit({ ...form, concessionType: form.concessionType });
  };

  // Reset form when modal opens, optionally with initial start time
  React.useEffect(() => {
    if (isOpen) {
      setForm({
        ...DEFAULT_FORM,
        startTime: initialStartTime || ''
      });
    }
  }, [isOpen, initialStartTime]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay-mobile" onClick={handleClose}>
      <div className="modal-container-mobile" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header-mobile">
          <h3 className="modal-title text-primary text-center">Add New Class</h3>
        </div>
        
        <div className="modal-body-mobile" style={{ position: 'relative' }}>
          {isCreating && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
                <p className="text-primary-600 font-medium">
                  {form.scheduleType === 'recurring' 
                    ? 'Creating recurring classes... This may take a moment.' 
                    : 'Creating class...'}
                </p>
              </div>
            </div>
          )}
          <div className="form-group">
            <div>
              <label className="form-label-small text-secondary">Class Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({...form, name: e.target.value})}
                className="form-input mt-1 block w-full px-3 py-2 rounded-lg"
                disabled={isCreating}
              />
            </div>
            
            <div>
              <label className="form-label-small text-secondary">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({...form, description: e.target.value})}
                rows={3}
                className="form-input mt-1 block w-full px-3 py-2 rounded-lg resize-vertical"
              />
            </div>
            
            <div>
              <label className="form-label-small text-secondary">Instructor</label>
              <input
                type="text"
                value={form.instructor}
                onChange={(e) => setForm({...form, instructor: e.target.value})}
                className="form-input mt-1 block w-full px-3 py-2 rounded-lg"
              />
            </div>
            
            <div>
              <label className="form-label-small text-secondary">Location</label>
              <input
                type="text"
                value={form.location}
                onChange={(e) => setForm({...form, location: e.target.value})}
                className="form-input mt-1 block w-full px-3 py-2 rounded-lg"
              />
            </div>
            
            <div>
              <label className="form-label-small text-secondary">Start Time</label>
              <input
                type="datetime-local"
                value={form.startTime}
                onChange={(e) => setForm({...form, startTime: e.target.value})}
                className="form-input mt-1 block w-full px-3 py-2 rounded-lg"
              />
            </div>
            
            {/* Schedule Type Selection */}
            <div>
              <label className="form-label-small text-secondary">Schedule Type</label>
              <select
                value={form.scheduleType}
                onChange={(e) => setForm({...form, scheduleType: e.target.value})}
                className="form-input mt-1 block w-full px-3 py-2 rounded-lg"
              >
                <option value="one-off">One-off Class</option>
                <option value="recurring">Recurring Schedule</option>
              </select>
            </div>

            {/* Recurring Schedule Fields */}
            {form.scheduleType === 'recurring' && (
              <>
                <div>
                  <label className="form-label-small text-secondary">Recurring Pattern</label>
                  <select
                    value={form.recurringPattern}
                    onChange={(e) => setForm({...form, recurringPattern: e.target.value})}
                    className="form-input mt-1 block w-full px-3 py-2 rounded-lg"
                  >
                    <option value="none">None (One-off)</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="custom">Custom (Specific Days)</option>
                  </select>
                </div>

                {/* Custom Days Selection */}
                {form.recurringPattern === 'custom' && (
                  <div>
                    <label className="form-label-small text-secondary">Select Days</label>
                    <div className="flex flex-wrap gap-2">
                      {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                        <label key={day} className="checkbox-item">
                          <input
                            type="checkbox"
                            checked={form.recurringDays.includes(day)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setForm({...form, recurringDays: [...form.recurringDays, day]});
                              } else {
                                setForm({...form, recurringDays: form.recurringDays.filter(d => d !== day)});
                              }
                            }}
                            className="mr-2"
                          />
                          <span className="text-sm text-secondary capitalize">{day}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Interval Selection */}
                {(form.recurringPattern === 'daily' || form.recurringPattern === 'weekly' || form.recurringPattern === 'monthly') && (
                  <div>
                    <label className="form-label-small text-secondary">
                      Every {form.recurringPattern === 'daily' ? 'day' : form.recurringPattern === 'weekly' ? 'week' : 'month'}
                    </label>
                    <input
                      type="number"
                      value={form.recurringInterval}
                      onChange={(e) => setForm({...form, recurringInterval: parseInt(e.target.value) || 1})}
                      min="1"
                      className="form-input mt-1 block w-full px-3 py-2 rounded-lg"
                    />
                  </div>
                )}

                {/* Recurring End Date */}
                <div>
                  <label className="form-label-small text-secondary">End Date (Optional - maximum 1 year)</label>
                  <input
                    type="date"
                    value={form.recurringEndDate}
                    onChange={(e) => setForm({...form, recurringEndDate: e.target.value})}
                    className="form-input mt-1 block w-full px-3 py-2 rounded-lg"
                  />
                </div>
              </>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label-small text-secondary">Duration (min)</label>
                <input
                  type="number"
                  value={form.duration}
                  onChange={(e) => setForm({...form, duration: parseInt(e.target.value)})}
                  className="form-input mt-1 block w-full px-3 py-2 rounded-lg"
                />
              </div>
              
              <div>
                <label className="form-label-small text-secondary">Max Capacity</label>
                <input
                  type="number"
                  value={form.maxCapacity}
                  onChange={(e) => setForm({...form, maxCapacity: parseInt(e.target.value)})}
                  className="form-input mt-1 block w-full px-3 py-2 rounded-lg"
                />
              </div>
            </div>

            <div>
              <label className="form-label-small text-secondary">Number of Concessions</label>
              <input
                type="number"
                value={form.concessionsRequired}
                onChange={(e) => setForm({...form, concessionsRequired: parseInt(e.target.value)})}
                min="1"
                className="form-input mt-1 block w-full px-3 py-2 rounded-lg"
              />
            </div>

            <div>
              <label className="form-label-small text-secondary">Concession Type *</label>
              <select
                value={form.concessionType}
                onChange={(e) => setForm({...form, concessionType: e.target.value})}
                className="form-input mt-1 block w-full px-3 py-2 rounded-lg"
                disabled={isCreating}
              >
                <option value="">— required —</option>
                {concessionTypes.filter(t => !t.isShared).map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.displayName}
                  </option>
                ))}
              </select>
              <p className="text-xs text-secondary mt-1">
                Every class draws from a named wallet. Members must be enrolled in this type and
                hold this wallet to see and book the class.
              </p>
            </div>
          </div>
        </div>
        
        <div className="modal-footer-mobile">
          <div className="modal-footer-content">
            <button
              onClick={handleClose}
              className="btn-secondary mr-4"
              disabled={isCreating}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="btn-primary btn-center-content"
              disabled={isCreating}
            >
              {isCreating ? (
                <>
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                  Creating...
                </>
              ) : (
                'Create Class'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

AddClassModal.displayName = 'AddClassModal';

export default AddClassModal;
